import moment from "moment";

/**
 * Visitor-module domain constants + pure derivation helpers.
 *
 * The stat cards, tab counts, filter dropdowns, and table badges on the
 * Visitors page must all agree on what a "status" or "type" is — these
 * live here (rather than inline in Visitor.js) so a rename can't drift
 * one consumer out of sync with the others.
 */

/**
 * Hours on-site (checked in, never out) after which a visitor is
 * flagged Overdue rather than On site.
 */
export const OVERDUE_AFTER_HOURS = 24;

export const VISITOR_STATUS = {
  ONSITE: "onsite",
  EXPECTED: "expected",
  DEPARTED: "departed",
  OVERDUE: "overdue",
};

export const VISITOR_STATUS_META = {
  [VISITOR_STATUS.ONSITE]:   { label: "On site",  className: "status-onsite" },
  [VISITOR_STATUS.EXPECTED]: { label: "Expected", className: "status-expected" },
  [VISITOR_STATUS.DEPARTED]: { label: "Departed", className: "status-departed" },
  [VISITOR_STATUS.OVERDUE]:  { label: "Overdue",  className: "status-overdue" },
};

/** Options for the status filter <Select>, in display order. */
export const VISITOR_STATUS_OPTIONS = [
  VISITOR_STATUS.ONSITE,
  VISITOR_STATUS.EXPECTED,
  VISITOR_STATUS.DEPARTED,
  VISITOR_STATUS.OVERDUE,
].map((value) => ({ value, label: VISITOR_STATUS_META[value].label }));

export const VISITOR_TYPES = {
  CONTRACTOR: "Contractor",
  GUEST: "Guest",
  DELIVERY: "Delivery",
  INSPECTOR: "Inspector",
};

/**
 * Wire values for visitor_info.visitor_type — the explicit type chosen
 * on the Add form. Distinct from VISITOR_TYPES above, which are display
 * labels (a plain "visitor" renders as the Guest badge).
 */
export const VISITOR_INFO_TYPES = {
  VISITOR: "visitor",
  CONTRACTOR: "contractor",
};

export const VISITOR_TYPE_CLASS = {
  [VISITOR_TYPES.CONTRACTOR]: "type-contractor",
  [VISITOR_TYPES.GUEST]: "type-guest",
  [VISITOR_TYPES.DELIVERY]: "type-delivery",
  [VISITOR_TYPES.INSPECTOR]: "type-inspector",
};

export const VISITOR_TABS = [
  { key: "visitors", label: "Visitors" },
  { key: "contractors", label: "Contractors" },
  { key: "inductions", label: "Inductions" },
  { key: "schedule", label: "Schedule" },
];

/**
 * Derive the lifecycle status from a visitor_info row.
 * checkout_time → departed; checkin_time → onsite, flipping to overdue
 * after OVERDUE_AFTER_HOURS; neither → expected.
 */
export const getVisitorStatus = (v) => {
  if (v.checkout_time) return VISITOR_STATUS.DEPARTED;
  if (v.checkin_time) {
    return moment().diff(moment(v.checkin_time), "hours") >= OVERDUE_AFTER_HOURS
      ? VISITOR_STATUS.OVERDUE
      : VISITOR_STATUS.ONSITE;
  }
  return VISITOR_STATUS.EXPECTED;
};

/**
 * Prefer the explicit visitor_type column (set by the Add form since
 * the scheduling feature). Legacy rows and self-service check-ins have
 * NULL there — fall back to keyword-matching the free-text
 * visit_purpose, passing unknown values through title-cased.
 */
export const getVisitorType = (v) => {
  if (v.visitor_type === VISITOR_INFO_TYPES.CONTRACTOR) return VISITOR_TYPES.CONTRACTOR;
  if (v.visitor_type === VISITOR_INFO_TYPES.VISITOR) return VISITOR_TYPES.GUEST;

  const p = (v.visit_purpose || "").toLowerCase();
  if (p.includes("contract")) return VISITOR_TYPES.CONTRACTOR;
  if (p.includes("deliver")) return VISITOR_TYPES.DELIVERY;
  if (p.includes("inspect")) return VISITOR_TYPES.INSPECTOR;
  if (!p || p.includes("guest") || p.includes("visit")) return VISITOR_TYPES.GUEST;
  return v.visit_purpose.charAt(0).toUpperCase() + v.visit_purpose.slice(1);
};

/**
 * Expected-today: scheduled to arrive on the current calendar day and
 * not yet checked in. Rows with no scheduled_arrival (legacy) fall back
 * to created_at, which was the pre-scheduling approximation.
 */
export const isVisitorExpectedToday = (v) => {
  if (getVisitorStatus(v) !== VISITOR_STATUS.EXPECTED) return false;
  const anchor = v.scheduled_arrival || v.created_at;
  return !!anchor && moment(anchor).isSame(moment(), "day");
};

/**
 * Induction lifecycle. Read straight from the server's `induction_summary`
 * (VisitorInductionService::summaryFor) — never inferred.
 *
 * This replaces a proxy that treated ANY form submission on the visitor row as
 * a completed induction. Because checking in through a form is what writes that
 * submission, every visitor who checked in showed as Complete without an
 * induction ever having been run.
 *
 * NOT_RECORDED is for visitors created before induction tracking existed. They
 * are not Pending — nobody failed to induct them, the system simply was not
 * keeping the record — so they sit out of the pending count entirely.
 *
 * EXPIRING is a warning, not a blocker: the induction is still valid but falls
 * inside the 30-day window (ExpiryStatus::WINDOW_DAYS), the same threshold the
 * contractor registers use. EXPIRED is past its validity and counts as pending
 * again.
 */
export const INDUCTION_STATE = {
  // Nothing was ever asked of this person: no requirement matches them and
  // their check-in form embeds no induction. Distinct from NOT_STARTED, which
  // means something IS outstanding.
  EMPTY: "empty",
  NOT_RECORDED: "not_recorded",
  NOT_STARTED: "not_started",
  // Submitted, waiting on a person to accept it. Not a synonym for "not done" —
  // the visitor has finished their part.
  PENDING: "pending",
  SENT: "sent",
  COMPLETED: "completed",
  EXPIRING: "expiring",
  EXPIRED: "expired",
  // Someone looked at the answers and refused them. Reads as not inducted, and
  // deliberately not the same as EXPIRED: one was refused, the other lapsed.
  REJECTED: "rejected",
};

export const INDUCTION_STATE_META = {
  [INDUCTION_STATE.EMPTY]:        { label: "—",            className: "induction empty" },
  [INDUCTION_STATE.NOT_RECORDED]: { label: "Not recorded", className: "induction not-recorded" },
  [INDUCTION_STATE.NOT_STARTED]:  { label: "Not started",  className: "induction not-started" },
  [INDUCTION_STATE.PENDING]:      { label: "Awaiting verification", className: "induction pending" },
  [INDUCTION_STATE.SENT]:         { label: "Link sent",    className: "induction sent" },
  [INDUCTION_STATE.COMPLETED]:    { label: "Completed",    className: "induction complete" },
  [INDUCTION_STATE.EXPIRING]:     { label: "Expiring soon", className: "induction expiring" },
  [INDUCTION_STATE.EXPIRED]:      { label: "Expired",      className: "induction expired" },
  [INDUCTION_STATE.REJECTED]:     { label: "Rejected",     className: "induction rejected" },
};

/**
 * The Induction filter on the Visitors page.
 *
 * Every badge state, in the order someone scans for them — what needs doing
 * first, then what is settled, then the two nothings. `outstanding` is not a
 * badge: it is the Pending-induction card and the Inductions tab as one filter,
 * for "show me everything waiting on somebody".
 *
 * The values are the states themselves, so the server can answer with the same
 * expressions the badge is built from.
 */
export const INDUCTION_FILTER_OPTIONS = [
  { value: "outstanding", label: "Needs attention" },
  { value: INDUCTION_STATE.PENDING, label: "Awaiting verification" },
  { value: INDUCTION_STATE.NOT_STARTED, label: "Not started" },
  { value: INDUCTION_STATE.SENT, label: "Link sent" },
  { value: INDUCTION_STATE.REJECTED, label: "Rejected" },
  { value: INDUCTION_STATE.EXPIRED, label: "Expired" },
  { value: INDUCTION_STATE.EXPIRING, label: "Expiring soon" },
  { value: INDUCTION_STATE.COMPLETED, label: "Completed" },
  { value: INDUCTION_STATE.EMPTY, label: "Not required" },
  { value: INDUCTION_STATE.NOT_RECORDED, label: "Not recorded" },
];

/** Whether this induction is submitted and waiting on someone to accept it. */
export const isAwaitingVerification = (summary) =>
  !!(summary && summary.state === INDUCTION_STATE.PENDING);

/**
 * The visitor's induction state — the state of their most recent induction.
 * Falls back to Not started for a row the server did not summarise (an older
 * backend, or a payload assembled without preloading): an induction shown as
 * outstanding is a nuisance, one hidden is a compliance hole. It must NOT fall
 * back to Pending, which now means "submitted, waiting on a verifier".
 */
export const getVisitorInductionState = (v) => {
  const state = v && v.induction_summary && v.induction_summary.state;
  return INDUCTION_STATE_META[state] ? state : INDUCTION_STATE.NOT_STARTED;
};

/**
 * What the badge reads.
 *
 * Once a property declares which inductions it requires, the useful answer is
 * "how many of them does this person hold" rather than the name of a state — so
 * the badge becomes "1 of 2". A property with no requirements keeps the plain
 * state label, exactly as before.
 */
export const getInductionBadgeLabel = (summary, state) => {
  const required = summary && summary.required_total;
  if (required > 0) {
    return `${summary.required_completed || 0} of ${required}`;
  }
  const meta = INDUCTION_STATE_META[state];
  return meta ? meta.label : "";
};

/**
 * The requirement badge can read "2 of 2" while the state is `pending` — they
 * hold everything, and one of them is still waiting on a person. The count
 * alone would say cleared, so the tooltip carries the rest of the sentence.
 */
export const getInductionBadgeHint = (state) => {
  if (state === INDUCTION_STATE.PENDING) {
    return "Submitted — waiting for someone to verify it";
  }
  if (state === INDUCTION_STATE.REJECTED) {
    return "Refused on review — a new induction needs to be run";
  }
  if (state === INDUCTION_STATE.EMPTY) {
    return "No induction is required for this visitor";
  }
  return "";
};
